import React, { useState, useRef, useEffect, useMemo, useId } from 'react';
import { Check, ChevronDown, Wand2 } from 'lucide-react';
import { apiClient } from '../utils/api';

// Claude/Anthropic icon
const ClaudeIcon = ({ className = "w-4 h-4" }) => {
    const id = useId();
    const clipId = `claude-clip${id}`;
    return (
        <svg className={className} viewBox="0 0 20 20" fill="none">
            <g clipPath={`url(#${clipId})`}>
                <path fill="#D97757" d="m3.908 13.296 3.945-2.206.066-.192-.066-.106H7.66l-.66-.04-2.254-.061-1.954-.081-1.894-.101-.477-.102-.447-.587.046-.293.4-.268.574.05 1.27.086 1.903.132 1.381.08 2.046.213h.325l.045-.131-.111-.081-.087-.081-1.97-1.33-2.13-1.407-1.117-.81-.604-.41-.305-.384-.132-.84.548-.602.737.05.187.051.747.572 1.594 1.23L7.4 7.173l.305.253.122-.086.015-.06-.137-.228-1.132-2.04L5.365 2.94l-.538-.86-.142-.517a2.5 2.5 0 0 1-.086-.607l.624-.845L5.568 0l.833.111.35.304.518 1.179.837 1.857 1.3 2.524.38.749.204.693.076.213h.132v-.122l.107-1.421.197-1.746.193-2.246.066-.633.315-.759.624-.41.488.233.4.572-.055.37-.239 1.542-.467 2.419-.304 1.619h.178l.203-.203.822-1.087 1.38-1.72.61-.684.71-.753.457-.36h.863l.635.941-.284.972-.889 1.123-.736.951-1.056 1.417-.66 1.133.061.091.158-.015 2.386-.506 1.289-.233 1.538-.263.696.324.076.329-.274.673-1.645.405-1.93.384-2.872.678-.036.025.04.051 1.295.121.553.03h1.356l2.523.188.66.435.396.531-.066.405-1.015.516-1.371-.324-3.198-.759-1.097-.273h-.152v.091l.914.89 1.675 1.508 2.097 1.943.106.48-.269.38-.284-.04-1.843-1.381-.71-.623-1.61-1.35h-.106v.141l.37.541 1.96 2.935.101.9-.142.294-.508.177-.558-.101-1.147-1.604-1.183-1.806-.954-1.62-.117.067-.564 6.046-.264.308-.609.233-.507-.384-.27-.623.27-1.23.324-1.603.264-1.275.239-1.584.142-.526-.01-.035-.117.015-1.198 1.64-1.822 2.453-1.442 1.538-.345.137-.6-.309.057-.551.335-.491 1.995-2.53L8 13.074l.777-.906-.006-.132h-.045l-5.3 3.43-.944.122-.406-.38.05-.622.193-.202 1.594-1.093z"/>
            </g>
            <defs>
                <clipPath id={clipId}>
                    <rect width="20" height="20" fill="#fff" rx="10"/>
                </clipPath>
            </defs>
        </svg>
    );
};

// Google Gemini icon (colorful star with gradient)
const GeminiIcon = ({ className = "w-4 h-4" }) => {
    const id = useId();
    const maskId = `gemini-mask${id}`;
    const gradId = `gemini-grad${id}`;
    const fillId = `gemini-fill${id}`;
    return (
        <svg className={className} viewBox="0 0 20 20" fill="none">
            <mask id={maskId} width="20" height="20" x="0" y="0" maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
                <path fill={`url(#${gradId})`} d="M10 0c.21 0 .392.143.443.347q.235.934.617 1.82a12.8 12.8 0 0 0 2.728 4.045 12.85 12.85 0 0 0 5.865 3.345.458.458 0 0 1 0 .886q-.934.235-1.82.617a12.8 12.8 0 0 0-4.045 2.728 12.85 12.85 0 0 0-3.345 5.865.458.458 0 0 1-.886 0 12.84 12.84 0 0 0-3.345-5.865 12.85 12.85 0 0 0-5.865-3.345.458.458 0 0 1 0-.886 12.84 12.84 0 0 0 5.865-3.345A12.84 12.84 0 0 0 9.557.347.46.46 0 0 1 10 0"/>
            </mask>
            <g mask={`url(#${maskId})`}>
                <rect width="20" height="20" fill={`url(#${fillId})`}/>
            </g>
            <defs>
                <linearGradient id={gradId} x1="5.685" x2="16.073" y1="13.382" y2="4.624" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#4893FC"/>
                    <stop offset="0.27" stopColor="#4893FC"/>
                    <stop offset="0.777" stopColor="#969DFF"/>
                    <stop offset="1" stopColor="#BD99FE"/>
                </linearGradient>
                <linearGradient id={fillId} x1="0" y1="0" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#4285F4"/>
                    <stop offset="0.5" stopColor="#9B72CB"/>
                    <stop offset="1" stopColor="#D96570"/>
                </linearGradient>
            </defs>
        </svg>
    );
};

// xAI Grok icon
const XaiIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <path fill="#000" d="M2 2l8.5 12.5L2 22h2.5l7-6.5L18.5 22H22l-8.5-12.5L22 2h-2.5l-7 6.5L5.5 2H2z"/>
    </svg>
);

// OpenAI GPT icon
const OpenAIIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <path fill="#000" d="M22.2819 9.8211C22.6519 8.4511 22.4519 6.9911 21.7319 5.7611C20.5719 3.7511 18.2019 2.7111 15.9119 3.1611C14.9619 2.0311 13.5619 1.3711 12.0719 1.3511C9.78189 1.3211 7.73189 2.8511 7.00189 5.0411C5.60189 5.3211 4.38189 6.1611 3.60189 7.3611C2.42189 9.3611 2.58189 11.8711 3.99189 13.7011C3.62189 15.0711 3.82189 16.5311 4.54189 17.7611C5.70189 19.7711 8.07189 20.8111 10.3619 20.3611C11.3119 21.4911 12.7119 22.1511 14.2019 22.1711C16.4919 22.2011 18.5419 20.6711 19.2719 18.4811C20.6719 18.2011 21.8919 17.3611 22.6719 16.1611C23.8519 14.1511 23.6919 11.6511 22.2819 9.8211ZM14.2019 20.7511C13.2219 20.7411 12.2919 20.3511 11.5919 19.6611L11.7919 19.5511L15.8619 17.2011C16.0819 17.0711 16.2219 16.8411 16.2219 16.5811V10.6811L17.9419 11.6711C17.9619 11.6811 17.9719 11.7011 17.9819 11.7211V16.6511C17.9819 18.9011 16.3219 20.7511 14.2019 20.7511ZM5.34189 17.0511C4.84189 16.1811 4.66189 15.1611 4.83189 14.1811L5.03189 14.2911L9.10189 16.6411C9.32189 16.7711 9.59189 16.7711 9.81189 16.6411L14.8519 13.7311V15.7111C14.8519 15.7311 14.8419 15.7611 14.8219 15.7711L10.7019 18.1511C8.86189 19.2111 6.52189 18.6311 5.34189 17.0511ZM4.16189 8.2011C4.66189 7.3311 5.43189 6.6611 6.34189 6.2911V11.3011C6.34189 11.5611 6.48189 11.7911 6.70189 11.9211L11.7419 14.8311L10.0219 15.8211C10.0019 15.8311 9.97189 15.8411 9.95189 15.8311L5.83189 13.4511C3.99189 12.3911 3.32189 10.0711 4.16189 8.2011ZM18.9319 11.6011L13.8919 8.6911L15.6119 7.7011C15.6319 7.6911 15.6619 7.6811 15.6819 7.6911L19.8019 10.0711C21.6419 11.1311 22.3119 13.4511 21.4719 15.3211C20.9719 16.1911 20.2019 16.8611 19.2919 17.2311V12.2211C19.2919 11.9611 19.1519 11.7311 18.9319 11.6011ZM20.6319 9.3411L20.4319 9.2311L16.3619 6.8811C16.1419 6.7511 15.8719 6.7511 15.6519 6.8811L10.6119 9.7911V7.8111C10.6119 7.7911 10.6219 7.7611 10.6419 7.7511L14.7619 5.3711C16.6019 4.3111 18.9419 4.8911 20.1219 6.4711C20.6219 7.3411 20.8019 8.3611 20.6319 9.3411ZM9.51189 12.8411L7.79189 11.8511C7.77189 11.8411 7.76189 11.8211 7.75189 11.8011V6.8711C7.75189 4.6211 9.41189 2.7711 11.5319 2.7711C12.5119 2.7811 13.4419 3.1711 14.1419 3.8611L13.9419 3.9711L9.87189 6.3211C9.65189 6.4511 9.51189 6.6811 9.51189 6.9411V12.8411ZM10.6119 10.7811L12.7319 9.5611L14.8519 10.7811V13.2211L12.7319 14.4411L10.6119 13.2211V10.7811Z"/>
    </svg>
);

export const DEFAULT_MODEL_ENTRY = { value: '', label: 'Default', group: 'default' };

export const FALLBACK_GROUP_LABELS = {
    default: 'Default',
    claude: 'Anthropic Claude',
    gemini: 'Google Gemini',
    openai: 'OpenAI GPT',
    xai: 'xAI Grok',
};

export const ModelIcon = ({ group, className = "w-4 h-4" }) => {
    switch (group) {
        case 'claude':
            return <ClaudeIcon className={className} />;
        case 'gemini':
            return <GeminiIcon className={className} />;
        case 'openai':
            return <OpenAIIcon className={className} />;
        case 'xai':
            return <XaiIcon className={className} />;
        default:
            return <Wand2 className={className} />;
    }
};

const ModelSelector = ({ selectedModel, onModelChange, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [models, setModels] = useState([DEFAULT_MODEL_ENTRY]);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        apiClient.getAvailableModels().then((data) => {
            if (data.models && data.models.length > 0) {
                setModels([DEFAULT_MODEL_ENTRY, ...data.models]);
            }
        });
    }, []);

    const selectedModelInfo = models.find(m => m.value === selectedModel) || models[0];

    const groups = useMemo(() => {
        const seen = new Map();
        const result = [];
        for (const m of models) {
            if (!seen.has(m.group)) {
                const label = m.group_label
                    || FALLBACK_GROUP_LABELS[m.group]
                    || m.group.charAt(0).toUpperCase() + m.group.slice(1);
                seen.set(m.group, label);
                result.push({ key: m.group, label });
            }
        }
        return result;
    }, [models]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleSelect = (value) => {
        onModelChange(value);
        setIsOpen(false);
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                ref={buttonRef}
                onClick={handleToggle}
                disabled={disabled}
                className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2 h-7 rounded-full text-xs sm:text-sm font-medium transition-all leading-none bg-[#0d0d0d05] sm:bg-transparent focus:outline-none active:scale-95 ${
                    disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : isOpen
                        ? 'text-brand-blue-600 sm:hover:bg-brand-blue-100 active:bg-brand-blue-100'
                        : 'text-gray-600 sm:hover:bg-app-hover active:bg-app-hover'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
            >
                <ModelIcon group={selectedModelInfo.group} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">{selectedModelInfo.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-[220px] max-h-[320px] overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {groups.map((group) => {
                        const groupModels = models.filter(m => m.group === group.key);
                        if (groupModels.length === 0) return null;

                        return (
                            <div key={group.key}>
                                {group.key !== 'default' && (
                                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                        {group.label}
                                    </div>
                                )}
                                {groupModels.map((model) => (
                                    <button
                                        key={model.value}
                                        onClick={() => handleSelect(model.value)}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors whitespace-nowrap ${
                                            selectedModel === model.value
                                                ? 'bg-brand-blue-50 text-brand-blue-600'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <ModelIcon group={model.group} className="w-4 h-4 flex-shrink-0" />
                                        <span className="flex-1">{model.label}</span>
                                        {selectedModel === model.value && (
                                            <Check className="w-4 h-4 text-brand-blue-600 flex-shrink-0" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ModelSelector;
